import React, { useEffect, useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { X } from 'lucide-react';

// Define the Toast type locally to avoid circular imports
interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
  action?: React.ReactNode;
}

const ToastComponent: React.FC<{ toast: Toast; onDismiss: () => void }> = ({
  toast,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300); // Allow animation to complete
  };

  return (
    <div
      className={`flex w-full max-w-md items-center justify-between space-x-4 rounded-md border p-4 shadow-md transition-all duration-300 ease-in-out
        ${toast.variant === 'destructive' ? 'border-red-200 bg-red-50 text-red-800' : 'border-gray-200 bg-white text-gray-800'}
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
    >
      <div className="grid gap-1">
        {toast.title && <h3 className="text-sm font-semibold">{toast.title}</h3>}
        {toast.description && <p className="text-sm opacity-90">{toast.description}</p>}
      </div>
      {toast.action}
      <button
        onClick={handleDismiss}
        className="rounded-md p-1 text-gray-500 hover:text-gray-900 focus:outline-none"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const Toaster = () => {
  const { toasts, dismiss } = useToast();

  if (!toasts.length) return null;

  return (
    <div className="fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]">
      {toasts.map((toast) => (
        <ToastComponent
          key={toast.id}
          toast={toast}
          onDismiss={() => dismiss(toast.id)}
        />
      ))}
    </div>
  );
};
